import { createContext } from 'react';
import type { TourDefinition } from './types';

/** Value provided by {@link TourProvider} and consumed via `useTours`. */
export interface TourContextValue {
  /** The bundled tour catalogue, in display order. */
  tours: ReadonlyArray<TourDefinition>;
  /** Launch a tour by id. No-op (and warns in dev) if the id is unknown. */
  startTour: (id: string) => void;
  /** Whether the given tour has already been completed/dismissed by this user. */
  hasSeen: (id: string) => boolean;
  /** The page tour bound to a pathname (e.g. `/releases/01H…`), if any. */
  pageTourFor: (pathname: string) => TourDefinition | undefined;
}

/**
 * Tour context.
 *
 * The default is inert (empty catalogue, no-op launcher) so any component can call
 * `useTours()` without a surrounding provider — isolated components and tests do
 * not need to mount the whole tour stack, mirroring the help context convention.
 */
export const TourContext = createContext<TourContextValue>({
  tours: [],
  startTour: () => undefined,
  hasSeen: () => false,
  pageTourFor: () => undefined,
});
